import type { ActorMatch, HubUser, MeIdentity, MeRole } from '@dyspatch/core';
import { adminClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { api, clearStoredActor, setStoredActor, storedActor, UnauthorizedError } from '@/lib/api';
import { passwordProblem } from '@/lib/password';

/**
 * The whole auth seam, still in one module on purpose.
 *
 * Since phase 5 the real path is Better Auth: email and password against
 * `/api/auth/*`, a signed HttpOnly cookie, and a session the hub can prove.
 * `GET /api/me` is still the probe, because it answers two questions the auth
 * cookie cannot - which `users` row this person is, and therefore which devices
 * are theirs - and it is what tells us whether the identity came from a session
 * or from a header.
 *
 * The legacy path is kept beside it, not replaced: `POST /session` trades the
 * admin token for the old cookie. Every script in `scripts/`, the
 * worker-enrollment tooling and the standing hub still speak it, so the gate
 * still offers it. It is the fallback, not the front door.
 */

type AuthClient = ReturnType<typeof createAuthClient>;

let cachedAuthClient: AuthClient | undefined;

/**
 * `baseURL` is the page's own origin rather than a build-time constant: the hub
 * serves this bundle, so whatever host reached it is the host the auth endpoints
 * are on, whether that is 127.0.0.1:8787 or a name in front of it.
 *
 * Built lazily, on first use, rather than at module scope: reading
 * `window.location.origin` while this module is only being evaluated, not used,
 * would throw for any import that happens outside a browser (a test importing a
 * route module to reach some other export of it, say), before any of that
 * import's own code runs. The client is still built exactly once, the first
 * time something here needs it.
 */
function getAuthClient(): AuthClient {
  if (!cachedAuthClient) {
    cachedAuthClient = createAuthClient({
      baseURL: window.location.origin,
      basePath: '/api/auth',
      plugins: [adminClient()],
    });
  }
  return cachedAuthClient;
}

export interface Session {
  actor: string;
  /**
   * The presentation tier `GET /api/me` reports: `member` owns devices, `viewer`
   * matched nothing and gets the metrics-only home. It decides what /me shows,
   * never what anyone is allowed to do.
   */
  role: MeRole;
  user: HubUser | null;
  /**
   * Whether the admin surfaces are drawn. The hub decides this, not the browser:
   * `GET /api/me` reports the same answer `requireAdminRole` acts on, so what the
   * console offers and what the server will serve cannot drift apart.
   */
  isAdmin: boolean;
  identity: MeIdentity;
  /**
   * How the hub resolved this name to a person. `/me` is the only page that
   * reads it, and only to explain an empty one: `ambiguous` means several
   * members answer to this name and the hub declined to pick, which is a
   * different thing from owning nothing.
   */
  match: ActorMatch;
  /**
   * The account is on a temporary password. The hub answers 403 to every
   * `/api` route but this probe until it is changed, so the shell sends every
   * route to the account page and the card there is what clears it.
   */
  mustChangePassword: boolean;
}

/** Resolves to null when there is no session yet, rather than throwing. */
export async function probeSession(): Promise<Session | null> {
  try {
    const me = await api.me();
    return {
      actor: me.actor,
      role: me.role,
      user: me.user,
      isAdmin: me.isAdmin,
      identity: me.identity,
      match: me.match,
      mustChangePassword: me.mustChangePassword,
    };
  } catch (error) {
    if (error instanceof UnauthorizedError) return null;
    throw error;
  }
}

/**
 * Better Auth's client returns `{ data, error }` rather than throwing, which is
 * convenient exactly once and a footgun everywhere else. Everything here throws,
 * so the gate has one `catch` and one place that renders a message.
 */
function unwrap(result: { error?: { message?: string | undefined } | null }, fallback: string) {
  if (result.error) throw new Error(result.error.message || fallback);
}

/**
 * The identity the hub just proved is the one it will name in the audit log, so
 * the self-reported name is cleared on the way in. Leaving it behind would mean
 * `lib/api.ts` kept sending `x-dyspatch-actor` on every request - harmless,
 * since the guard ignores it for a session, but the browser should not still be
 * carrying a claim nobody reads.
 */
async function afterCredential(): Promise<Session> {
  const session = await probeSession();
  if (!session) throw new Error('the hub accepted the credential but will not identify us');
  return session;
}

export async function signIn(email: string, password: string): Promise<Session> {
  unwrap(
    await getAuthClient().signIn.email({ email, password }),
    'that email and password did not work',
  );
  clearStoredActor();
  return afterCredential();
}

/**
 * The bootstrap. The hub allows this exactly while it has no accounts, and the
 * one it creates is an admin; after that it answers 403 with a sentence saying
 * so, which is what the gate shows. There is deliberately no client-side check
 * of "is this the first account" - the server is the only thing that knows.
 */
export async function signUpFirstAdmin(
  name: string,
  email: string,
  password: string,
): Promise<Session> {
  unwrap(
    await getAuthClient().signUp.email({ name, email, password }),
    'the hub refused that sign-up',
  );
  clearStoredActor();
  return afterCredential();
}

/**
 * The legacy path, unchanged. The name is stored *before* the token is exchanged
 * so the `/api/me` call that follows already carries it; the hub resolves the
 * actor to a user row, and a login that raced the header would come back as a
 * "viewer" who owns nothing.
 */
export async function loginWithToken(token: string, actor: string): Promise<Session> {
  const name = actor.trim();
  if (!name) throw new Error('a name is needed for the audit log');
  setStoredActor(name);
  try {
    await api.login(token);
  } catch (error) {
    clearStoredActor();
    throw error;
  }
  return afterCredential();
}

/**
 * A new password, chosen by the person. `revokeOtherSessions` closes every other
 * session on the account, which is the point of changing a password that
 * somebody else may have typed; Better Auth issues this browser a fresh cookie
 * in the same response, so the person stays signed in here. The hub clears the
 * temporary-password flag on the way through, and the caller re-probes the
 * session to see that.
 *
 * The browser's own checks run first so a mismatch or a short password is said
 * without a round trip, and the current password is left to the hub, which is
 * the only thing that can verify it.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  confirm: string,
): Promise<void> {
  const problem = passwordProblem(newPassword, confirm);
  if (problem) throw new Error(problem);
  unwrap(
    await getAuthClient().changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    }),
    'the hub did not accept that password',
  );
}

/**
 * Both credentials are dropped, whichever one got us in. A browser can hold the
 * Better Auth cookie and the legacy one at once (somebody who used the token
 * gate and then signed in), and signing out of one while the other still opens
 * the app would look like the button did nothing.
 */
export async function logout(): Promise<void> {
  await getAuthClient()
    .signOut()
    .catch(() => undefined);
  await api.logout().catch(() => undefined);
  clearStoredActor();
}

export { storedActor };
